import {
	Button,
	ChevronDown,
	Combobox,
	type ComboboxOption,
	chipGeometryClass,
	chipHoverSurfaceClass,
	cn,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@ondit/ui";
import { Check } from "@ondit/ui/icons";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { api } from "~/core/api/index";
import { useT } from "~/core/i18n";
import { read } from "~/core/query";

/** The four the server maps to a thinking budget; see `agents::chats::Effort`. */
export type Effort = "off" | "low" | "medium" | "high";

interface ComposerModelState {
	/** `null` is "let the server pick", which is what most turns should be. */
	model: string | null;
	effort: Effort;
	setModel: (model: string | null) => void;
	setEffort: (effort: Effort) => void;
}

/**
 * What the composer's chips are set to, persisted per browser.
 *
 * Persisted rather than per-chat: somebody who picked a model because the
 * default was wrong for their work wants that choice on the next question too,
 * and re-picking it every turn is how a setting gets abandoned. It is not
 * persisted per workspace — the reason for a choice is the kind of work, not
 * the brand.
 */
export const useComposerModel = create<ComposerModelState>()(
	persist(
		(set) => ({
			model: null,
			effort: "off",
			setModel: (model) => set({ model }),
			setEffort: (effort) => set({ effort }),
		}),
		{ name: "ondit-composer-model" },
	),
);

/** Read outside React, for the request builder. */
export function composerModelRequest(): { model?: string; effort: Effort } {
	const { model, effort } = useComposerModel.getState();
	return { ...(model ? { model } : {}), effort };
}

/** The name of each budget and what picking it means, as `t` keys. */
const EFFORT_LABELS: Record<Effort, [string, string]> = {
	off: ["Off", "Answer directly"],
	low: ["Low", "A moment to plan"],
	medium: ["Medium", "Everyday work"],
	high: ["High", "Work through it"],
};

/** Not a model id, so it can never collide with one. */
const AUTOMATIC = "\u0000automatic";

/* These sit on the composer's own button row, beside `+` and `/`, so they are
 * the same control: the bare 30px pill — no fill, no border, hover surface
 * only. A bordered chip next to two borderless ones reads as a field that
 * wandered into a toolbar. The combobox is skinned to match rather than the
 * reverse, since it is the one with chrome of its own to take off. */
const CHIP = cn(
	chipGeometryClass,
	chipHoverSurfaceClass,
	"flex text-[var(--text-secondary)] hover-hover:text-[var(--text-primary)]",
);

function Selected({ on }: { on: boolean }) {
	return on ? (
		<Check className="size-[13px] shrink-0 text-[var(--text-primary)]" />
	) : (
		<span className="size-[13px] shrink-0" aria-hidden />
	);
}

/**
 * The model and the effort, as two chips on the composer's button row.
 *
 * They replace a line of status text that said which model would answer but
 * gave no way to change it. Both are things a person decides per question
 * rather than per account, which is why they sit on the composer and not in
 * settings — the moment you want a different model is the moment you are
 * typing the question that needs one.
 *
 * The model is a searchable combobox, not a menu. The gateway offers 246 models
 * from about forty vendors: a menu of that is a scroll, and grouping it by
 * vendor only adds forty headers to scroll past. Every `display_name` already
 * reads "Vendor: Model", so typing the vendor filters to it — which is what a
 * group header was going to be used for anyway.
 *
 * Automatic is pinned first and is the default. Naming a model here overrides
 * the server's choice for every turn until it is set back, so the chip says
 * which one is answering even when nobody has chosen.
 */
export function ComposerModelControls() {
	const { data, error } = useQuery({
		queryKey: ["composer-models"],
		queryFn: () => api.models(),
		...read,
		staleTime: 5 * 60 * 1000,
	});
	const t = useT();
	const { model, effort, setModel, setEffort } = useComposerModel();
	const details = data?.modelDetails;
	const options = useMemo<ComboboxOption[]>(
		() => [
			{
				value: AUTOMATIC,
				/* The model that will answer, named — not "Automatic · <model>", which
           spent a third of the chip saying that nobody had chosen. The word is
           the fallback for the moment before the gateway's default arrives. */
				label: data?.defaultModel || t("Automatic"),
			},
			...(details ?? []).map((row) => ({
				value: row.model,
				label: row.display_name || row.model,
			})),
		],
		[details, data?.defaultModel, t],
	);

	if (error)
		return (
			<span className="px-2 text-[var(--text-muted)] text-xs" role="status">
				{t("Unable to load models")}
			</span>
		);
	if (!data)
		return (
			<span className="px-2 text-[var(--text-muted)] text-xs" role="status">
				{t("Loading models…")}
			</span>
		);

	const effortName = t(EFFORT_LABELS[effort][0]);

	return (
		<>
			{/* The combobox's own wrapper is `w-full`, so as a flex item it claimed the
          whole row and squeezed the chips beside it. Shrink-to-fit here instead:
          the chip is as wide as the model's name, capped so a vendor's longest
          display name clips rather than taking the row. */}
			<div className="flex min-w-0 max-w-[230px]">
				<Combobox
					size="sm"
					searchable
					value={model ?? AUTOMATIC}
					options={options}
					onChange={(next) => setModel(next === AUTOMATIC ? null : next)}
					searchPlaceholder={t("Search models")}
					emptyMessage={t("No model matches")}
					inputProps={{ "aria-label": t("Model") }}
					/* The list is 246 models and a search box; it stops following the
             trigger once the trigger is only as wide as a short model name. */
					dropdownWidth={280}
					className={cn(CHIP, "border-transparent bg-transparent")}
				/>
			</div>

			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button
						variant="quiet"
						className={cn(CHIP, "shrink-0", effort !== "off" && "text-[var(--text-primary)]")}
						aria-label={t("Thinking")}
					>
						<span>
							{t("Thinking")}
							{effort === "off" ? "" : ` · ${effortName}`}
						</span>
						<ChevronDown className="size-[12px] shrink-0 text-[var(--text-muted)]" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent>
					{(Object.keys(EFFORT_LABELS) as Effort[]).map((value) => {
						const [name, hint] = EFFORT_LABELS[value];
						return (
							<DropdownMenuItem key={value} onSelect={() => setEffort(value)}>
								<Selected on={effort === value} />
								<span className="font-medium">{t(name)}</span>
								<span className="text-[var(--text-muted)]">{t(hint)}</span>
							</DropdownMenuItem>
						);
					})}
				</DropdownMenuContent>
			</DropdownMenu>
		</>
	);
}
